import React, {useMemo, useState} from 'react'
import {Lang} from 'yougile-lang'
import {SearchUtil} from 'yougile-util'
import {DropdownMenuContainer} from '@components/common/base/dropdown-button-menu/dropdown-menu-container'
import {DropdownMenuItemCheckbox} from '@components/common/base/dropdown-button-menu/dropdown-menu-item-checkbox'
import {InputNew} from '@components/common/base/input-new'
import {Select} from '@components/common/base/select'
import {Variant} from '@components/common/base/select/types'
import {TippyProps} from '@tippyjs/react'
import {OptionsItemCheckbox} from '../dropdown-button-menu/types'

export interface SelectWithCheckboxProps {
  options: OptionsItemCheckbox[]
  value?:
    | React.ReactNode
    | ((options: OptionsItemCheckbox[]) => React.ReactNode)
  placeholder?: string
  size?: '32' | '36' | '40'
  disabled?: boolean
  allCaption?: string
  onChange: (data: OptionsItemCheckbox[]) => void
  className?: string
  innerClassName?: string
  labelStyle?: string
  variant?: Variant
  search?: boolean
  link?: boolean
  placement?: TippyProps['placement']
  onlyOne?: boolean
  /**
   * Allows to completely customize the logic of creating a caption
   * @param item
   * @param allCaption
   * @param placeholder
   */
  getCaption?: (
    item: OptionsItemCheckbox[],
    allCaption: string,
    placeholder: string
  ) => string
}

export const SelectWithCheckboxes = (props: SelectWithCheckboxProps) => {
  const {
    innerClassName,
    options,
    size = '32',
    onlyOne,
    allCaption,
    search,
    link,
    placement,
    getCaption
  } = props
  const [open, setOpen] = useState(false)

  const displayValue =
    props.value ??
    (getCaption || getValue)(
      options,
      allCaption,
      props.placeholder ?? allCaption
    )

  const onToggle = (item: OptionsItemCheckbox) => {
    if (onlyOne) {
      options.forEach(o => {
        if (o.id === item.id) {
          return
        }
        o.selected = false
      })
      item.selected = !item.selected
    } else if (item.key === 'all') {
      const newValue = getAllSelectedNewValue(item.selected)
      options.forEach(o => {
        o.selected = newValue
      })
    } else {
      item.selected = !item.selected
    }
    props.onChange?.(options)
  }

  const [searchStr, setSearchStr] = useState('')
  const displayedOptions = useMemo(() => {
    if (!searchStr) {
      return options
    }
    const words = SearchUtil.prepareWords(SearchUtil.cleanStr(searchStr))
    return options.filter(x =>
      SearchUtil.hasAllWords(SearchUtil.cleanStr(x.text), words)
    )
  }, [searchStr, options])

  return (
    <Select
      className={props.className}
      variant={props.variant}
      link={link}
      placement={placement}
      content={
        <DropdownMenuContainer className={innerClassName}>
          {search && (
            <div className="px-12 py-8 relative">
              <InputNew
                value={searchStr}
                onChange={setSearchStr}
                onFocus={e =>
                  e.target.setSelectionRange(0, e.target.value.length)
                }
                iconLeft="IconSearch"
                iconRight={searchStr ? 'IconX' : undefined}
                onIconRightClick={() => setSearchStr('')}
              />
            </div>
          )}
          {!!allCaption && !searchStr && (
            <DropdownMenuItemCheckbox
              key="all"
              item={{
                text: allCaption,
                key: 'all',
                selected: getAllSelectedValue(options)
              }}
              size={size}
              onToggle={onToggle}
            />
          )}
          {searchStr && !displayedOptions.length && (
            <div className="px-12 py-8 text-primary text-sm-regular">
              {Lang.msg.emptySearchResults}
            </div>
          )}
          {displayedOptions.map(item => (
            <DropdownMenuItemCheckbox
              key={item.id || item.key}
              item={item}
              size={size}
              onToggle={onToggle}
            />
          ))}
        </DropdownMenuContainer>
      }
      open={open}
      onOpenChange={v => setOpen(v)}
      placeholder={props.placeholder}
      disabled={props.disabled}
    >
      {typeof displayValue === 'function'
        ? displayValue(options)
        : displayValue}
    </Select>
  )
}

export const getValue = (
  options: OptionsItemCheckbox[],
  allCaption?: string,
  placeholder?: string
): string | undefined => {
  if (!options?.length) {
    return allCaption ?? Lang.msg.all
  }

  if (options?.length && options.every(o => !o.selected)) {
    return placeholder ?? Lang.msg.all
  }

  const allSelected = options.every(o => o.selected)
  if (allSelected) {
    return allCaption ?? Lang.msg.all
  }

  return options
    .filter(s => s.selected)
    .map(s => s.text)
    .join(', ')
}

const getAllSelectedValue = (
  options: OptionsItemCheckbox[]
): boolean | undefined => {
  const allSelected = options.every(o => o.selected)
  const notSelected = options.every(o => !o.selected)
  const anySelected = options.some(o => o.selected)
  const semiselected = !allSelected && !notSelected && anySelected
  return semiselected ? undefined : allSelected
}

const getAllSelectedNewValue = (prevValue?: boolean): boolean => {
  if (prevValue === undefined) {
    return true
  }

  return !prevValue
}
